/**
 * (c) 2023, Center for Computational Thinking and Design at Aarhus University and contributors
 * Modifications (c) 2024, Micro:bit Educational Foundation and contributors
 *
 * SPDX-License-Identifier: MIT
 */
import { generateBlob } from "@microbit/ml-header-generator";
import { ActionName, actionNamesFromLabels } from "./utils";
import { DatasetEditorJsonFormat, ActionData } from "../model";
import { mlSettings } from "../mlConfig";
import { DataWindow } from "../project-utils";

const createMlEvents = (actionNames: ActionName[]) => {
  let code = "";
  actionNames.forEach((an, idx) => {
    const stringValue = JSON.stringify(an.actionLabel);
    code += `  //% fixedInstance block=${stringValue}\n`;
    code += `  export const ${an.actionVar} = new MlEvent(${
      idx + 2
    }, ${stringValue});\n`;
  });
  return code;
};

const createEventListeners = (actionNames: ActionName[]) => {
  actionNames.unshift({
    actionLabel: "unknown",
    actionVar: "Unknown",
  });
  const totalActions = actionNames.length;
  let code = "";
  for (let i = 0; i < totalActions; i++) {
    code += `  control.onEvent(MlRunnerIds.MlRunnerInference, ${
      i + 1
    }, () => {\n`;
    code += `    if (!event.${actionNames[i].actionVar}.onStartHandler) {\n`;
    code += `      maybeUpdateEventStats(event.${actionNames[i].actionVar});\n`;
    code += `    }\n`;
    code += `  });\n`;
  }
  return code;
};

const arrayBufferToHexString = (input: Uint8Array): string =>
  Array.from(input, (i) => i.toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase();

export const getAutogeneratedTs = (
  actions: ActionData[],
  machineCode: Uint8Array,
  dataWindow: DataWindow
) => {
  const customHeaderBlob = generateBlob({
    samples_period: dataWindow.deviceSamplesPeriod,
    samples_length: dataWindow.deviceSamplesLength,
    sample_dimensions: 3,
    actions: actions.map((a) => ({
      label: a.name,
      threshold: a.requiredConfidence ?? mlSettings.defaultRequiredConfidence,
    })),
  });
  const headerHexString = arrayBufferToHexString(
    new Uint8Array(customHeaderBlob)
  );
  const modelHexString = arrayBufferToHexString(machineCode);
  const actionNames = actionNamesFromLabels(actions.map((a) => a.name));

  return `// Auto-generated. Do not edit.
namespace ml {
  export namespace event {
    ${createMlEvents(actionNames)}
    }
    
  events = [event.Unknown,${actionNames
    .map((an) => `event.${an.actionVar}`)
    .join(",")}];
    
${createEventListeners(actionNames)}
  getModelBlob = (): Buffer => {
    const result = hex\`${headerHexString + modelHexString}\`;
    return result;
  };

  simulatorSendData();
  startRunning();
}

// Auto-generated. Do not edit. Really.
`;
};

export const getDatasetJson = (gs: DatasetEditorJsonFormat) => {
  return JSON.stringify(gs, null, 2);
};
